import {
  Box,
  Button,
  Container,
  Title,
  TransferList,
  TransferListData,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { Check, X } from 'tabler-icons-react';
import { OpenComponent } from '../../../enums/misc.enum';
import { removeEmptyPropertiesWithNested } from '../../../helpers/filters.helpers';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { ErrorResponse } from '../../../type/error-response.types';
import {
  EntityFilterData,
  EntityIdFilterData,
  SelectData,
} from '../../../type/filter.types';
import { BasicMIDs } from '../../../type/mids.types';
import { RetrieveAllMIDsPOSsSelectionResponse } from '../../../type/pos.types';
import { BasicPos } from '../../../type/select.types';
import BackButton from '../../buttons/back-button.component';
import SelectBranchFilter from '../../filters/select-branch-filter.component';
import XPaper from '../../x-components/x-paper.component';

const initialValues: TransferListData = [[], []];

function AssignMids(props: Props) {
  const [data, setData] = useState<TransferListData>(initialValues);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { setComponentManager, midId, mids } = props;
  const axiosPrivate = useAxiosPrivate();
  const filterSelectData = useForm<EntityFilterData>({
    initialValues: {
      resellersData: [],
      merchantsData: [],
      branchesData: [],
    },
  });
  const filterValues = useForm<EntityIdFilterData>({
    initialValues: {
      reseller: '',
      merchant: '',
      branch: '',
    },
  });
  const [movedToAssignedPOSs, setMovedToAssignedPOss] = useState<number[]>([]);
  const selectedMid = mids?.find((mid) => mid.midId === midId);
  const [originalAssignedPOSs, setOriginalAssignedPOSs] = useState<number[]>(
    []
  );

  useEffect(() => {
    filterValues.setFieldValue('merchant', selectedMid?.merchantId.toString());
  }, []);

  const loadPOSs = async () => {
    setData(initialValues);
    const params = removeEmptyPropertiesWithNested({
      merchantId: selectedMid?.merchantId.toString(),
      branchId: filterValues.values.branch,
      midId: selectedMid?.midId,
    });

    try {
      setIsInitialLoading(true);
      const response = await axiosPrivate.get('/web/select/pos_mids', {
        params,
      });

      const data: RetrieveAllMIDsPOSsSelectionResponse = response.data;

      const assignedToMIDPOSs: SelectData[] = data.assignedToMIDPOSs.map(
        (merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        }
      );

      const notAssignedToMIDPOSs: SelectData[] = data.notAssignedToMIDPOSs.map(
        (merchant: BasicPos) => {
          return {
            label: merchant.posName,
            value: merchant.posId.toString(),
          };
        }
      );

      const assignedToMIDPOSsIds = assignedToMIDPOSs.map((pos: SelectData) => {
        return parseInt(pos.value);
      });

      setOriginalAssignedPOSs(assignedToMIDPOSsIds);

      setData([notAssignedToMIDPOSs, assignedToMIDPOSs]);
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-retrieve-pos-data',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsInitialLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const newAssignedPOSs = data[1].filter((pos) => {
        return !originalAssignedPOSs?.includes(parseInt(pos.value));
      });

      const newAssignedPOSsIds = newAssignedPOSs.map((pos) => {
        return parseInt(pos.value);
      });

      const response = await axiosPrivate.post('/web/mids/assign', {
        posIds: newAssignedPOSsIds,
        midId: selectedMid?.midId,
      });

      loadPOSs();
      setMovedToAssignedPOss([]);

      showNotification({
        id: 'successfully-assigned-mids',
        message: 'Successfully assigned POS(s) to MID',
        icon: <Check size={16} />,
        color: 'teal',
        autoClose: 6000,
      });
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-assign-pos-data',
        message: error.error || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPOSs();
  }, [filterValues.values.branch]);

  return (
    <>
      <div style={{ flex: 1, margin: 0 }}>
        <BackButton
          handleClick={() => {
            setComponentManager(OpenComponent.HOME);
          }}
        />
        <Title order={2} my='lg'>
          Assign MIDs
        </Title>
        <XPaper>
          <Container p='md' fluid>
            <SelectBranchFilter
              filterSelectData={filterSelectData}
              filterValues={filterValues}
              isMerchantDependent={true}
            />
          </Container>

          <Container p='md' fluid>
            <TransferList
              showTransferAll={false}
              value={data}
              onChange={(value: TransferListData) => {
                setData(value);
                const newAssignedPOSs = value[1].filter((pos) => {
                  return !originalAssignedPOSs?.includes(parseInt(pos.value));
                });

                const newAssignedPOSsIds = newAssignedPOSs.map((pos) => {
                  return parseInt(pos.value);
                });

                setMovedToAssignedPOss(newAssignedPOSsIds);
              }}
              searchPlaceholder='Search...'
              nothingFound={isInitialLoading ? 'Loading...' : 'Nothing here'}
              titles={['Unassigned POS(s)', 'Assigned POS(s)']}
              breakpoint='sm'
            />
            <Box
              my='md'
              style={{ display: 'flex', justifyContent: 'flex-end' }}
            >
              <Button
                loading={isLoading}
                onClick={handleSubmit}
                disabled={movedToAssignedPOSs.length === 0}
                data-cy='submitButton'
                type='submit'
              >
                Submit
              </Button>
            </Box>
          </Container>
        </XPaper>
      </div>
    </>
  );
}

interface Props {
  setComponentManager: React.Dispatch<React.SetStateAction<OpenComponent>>;
  midId: number;
  mids: BasicMIDs[] | undefined;
}

export default AssignMids;
